import moment from "moment-timezone";
import "moment/locale/es";
import { Base64 } from "js-base64";

moment.locale("es");

export const EVENT_TYPE_OPTIONS = [
  { value: "ONLINE", label: "En línea", icon: "mdi-monitor" },
  { value: "IN_PERSON", label: "Presencial", icon: "mdi-map-marker" },
  { value: "HYBRID", label: "Híbrido", icon: "mdi-earth" },
];

export const EVENT_TYPE_LABEL = EVENT_TYPE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

export const resolveEventTypeLabel = (value) => EVENT_TYPE_LABEL[value] || value || "—";

export const resolveEventTypeIcon = (value) => {
  const found = EVENT_TYPE_OPTIONS.find((opt) => opt.value === value);
  return found ? found.icon : "mdi-calendar";
};

const unwrap = (item) => (item && item.event ? item.event : item) || {};
const DEFAULT_EVENT_TIME_ZONE = "America/Mexico_City";

const getCalendarDate = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }

  const parsed = moment.utc(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const normalizeTime = (value) => {
  if (!value) return null;

  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const getEventTimeZone = (dateConfiguration) => {
  const configuredTimeZone = dateConfiguration && dateConfiguration.timeZone;
  return configuredTimeZone && moment.tz.zone(configuredTimeZone)
    ? configuredTimeZone
    : DEFAULT_EVENT_TIME_ZONE;
};

const getEventDateTime = (item, dateField, timeField) => {
  const ev = unwrap(item);
  const dateConfiguration = ev.dateConfiguration;
  if (!dateConfiguration || !dateConfiguration[dateField]) return null;

  const calendarDate = getCalendarDate(dateConfiguration[dateField]);
  const time = normalizeTime(dateConfiguration[timeField]);
  if (calendarDate && time) {
    const configuredDateTime = moment.tz(
      `${calendarDate} ${time}`,
      "YYYY-MM-DD HH:mm",
      getEventTimeZone(dateConfiguration)
    );
    if (configuredDateTime.isValid()) return configuredDateTime;
  }

  const fallback = moment(dateConfiguration[dateField]);
  return fallback.isValid() ? fallback : null;
};

export const getEventId = (item) => {
  const ev = unwrap(item);
  return ev._id || (item && item._id) || null;
};

export const getEventTitle = (item) => {
  const ev = unwrap(item);
  return ev.name || "Evento sin título";
};

const looksLikePlainText = (value) => {
  if (value.length < 4) return true;
  if (/<[a-z][\s\S]*?>/i.test(value)) return true;
  if (/^&[a-z]+;/i.test(value)) return true;
  if (/\s/.test(value)) return true;
  if (/^[\s\r\n]*[\{\[]/.test(value)) return true;
  return !/^[A-Za-z0-9+/=_-]+$/.test(value);
};

export const decodeEventDescription = (value) => {
  if (typeof value !== "string" || value.length === 0) return value || "";
  if (looksLikePlainText(value)) return value;

  try {
    const decoded = Base64.decode(value);
    return decoded || value;
  } catch (error) {
    return value;
  }
};

export const getEventDescription = (item) => {
  const ev = unwrap(item);
  return decodeEventDescription(
    ev.description || ev.summary || ev.shortDescription || ""
  );
};

export const getEventThumbnail = (item) => {
  const ev = unwrap(item);
  if (!ev) return null;
  return ev.banner || ev.image || ev.thumbnail || ev.cover || null;
};

export const getEventCategory = (item) => {
  const ev = unwrap(item);
  return ev.category || null;
};

export const getEventType = (item) => {
  const ev = unwrap(item);
  return ev.type || null;
};

export const getEventTags = (item) => {
  const ev = unwrap(item);
  if (Array.isArray(ev.tags)) return ev.tags;
  if (typeof ev.tags === "string") return [ev.tags];
  return [];
};

export const getEventLocation = (item) => {
  const ev = unwrap(item);
  if (ev.place) return ev.place;
  if (ev.location && ev.location.name) return ev.location.name;
  if (ev.venue && ev.venue.name) return ev.venue.name;
  return null;
};

export const getEventStartDate = (item) => {
  const ev = unwrap(item);
  return ev.dateConfiguration && ev.dateConfiguration.startDate
    ? ev.dateConfiguration.startDate
    : null;
};

export const getEventEndDate = (item) => {
  const ev = unwrap(item);
  return ev.dateConfiguration && ev.dateConfiguration.endDate
    ? ev.dateConfiguration.endDate
    : null;
};

export const getEventStartMoment = (item) =>
  getEventDateTime(item, "startDate", "startTime");

export const getEventEndMoment = (item) =>
  getEventDateTime(item, "endDate", "endTime");

export const isUpcoming = (item) => {
  const start = getEventStartMoment(item);
  if (!start) return false;
  return start.isSameOrAfter(moment());
};

export const isLive = (item) => {
  const start = getEventStartMoment(item);
  const end = getEventEndMoment(item);
  if (!start) return false;
  const now = moment();
  if (end) {
    return start.isSameOrBefore(now) && end.isSameOrAfter(now);
  }
  return start.isSame(now, "day");
};

export const formatEventDate = (item, format = "DD MMM YYYY") => {
  const start = getEventStartMoment(item);
  if (!start) return "";
  return start.format(format);
};

export const formatEventTime = (item) => {
  const start = getEventStartMoment(item);
  if (!start) return "";
  const end = getEventEndMoment(item);
  const startLabel = start.format("HH:mm");
  const endLabel = end ? end.format("HH:mm") : null;
  return endLabel && endLabel !== startLabel
    ? `${startLabel}h — ${endLabel}h`
    : `${startLabel}h`;
};

export const formatEventDateRange = (item) => {
  const start = getEventStartMoment(item);
  const end = getEventEndMoment(item);
  if (!start) return "";
  const startLabel = start.format("DD MMM");
  if (!end) return startLabel;
  const endLabel = end.format("DD MMM YYYY");
  return `${startLabel} — ${endLabel}`;
};

export const formatEventDateFull = (item) => {
  const start = getEventStartMoment(item);
  if (!start) return "";
  const startLabel = start.format("DD MMM");
  const end = getEventEndMoment(item);
  if (end) {
    const endLabel = end.format("DD MMM");
    return `${startLabel} — ${endLabel} · ${formatEventTime(item)}`;
  }
  return `${startLabel} · ${formatEventTime(item)}`;
};

export const resolveEventDetailRoute = (item) => {
  const id = getEventId(item);
  if (!id) return "/member-v2/events";
  return `/member-v2/events/${id}`;
};

export const applyEventFilters = (events, filters) => {
  if (!Array.isArray(events)) return [];
  const search = (filters.search || "").trim().toLowerCase();
  const categories = filters.categories || [];
  const types = filters.types || [];
  const tags = filters.tags || [];
  const from = filters.from || null;
  const to = filters.to || null;

  return events.filter((item) => {
    const ev = unwrap(item);
    if (!ev) return false;
    if (search && !(ev.name || "").toLowerCase().includes(search)) return false;
    if (categories.length > 0 && !categories.includes(ev.category)) return false;
    if (types.length > 0 && !types.includes(ev.type)) return false;
    if (tags.length > 0) {
      const itemTags = getEventTags(item);
      const hasTag = tags.some((t) => itemTags.includes(t));
      if (!hasTag) return false;
    }
    const start = getEventStartMoment(item);
    if (from && start && start.isBefore(moment(from))) return false;
    if (to && start && start.isAfter(moment(to).endOf("day"))) return false;
    return true;
  });
};

export const sortEventsByDate = (events, order = "asc") => {
  const list = [...(events || [])];
  list.sort((a, b) => {
    const sa = getEventStartMoment(a);
    const sb = getEventStartMoment(b);
    if (!sa && !sb) return 0;
    if (!sa) return 1;
    if (!sb) return -1;
    return order === "asc" ? sa.valueOf() - sb.valueOf() : sb.valueOf() - sa.valueOf();
  });
  return list;
};

export const extractEventOptions = (filtersData) => {
  if (!filtersData) return { categories: [], tags: [], types: [] };
  return {
    categories: Array.isArray(filtersData.categories) ? filtersData.categories : [],
    tags: Array.isArray(filtersData.tags) ? filtersData.tags : [],
    types: Array.isArray(filtersData.types) ? filtersData.types : [],
  };
};

export const isEventLockedForUser = (user) => {
  if (!user) return true;
  const status = user.validationStatus;
  return status === "EXPIRED" || status === "INACTIVO";
};
